// modules/inicio/pages/OfflineBanner.js
// Banner flotante único para TODO el sistema (no uno por módulo) — mismo
// comportamiento que el OfflineBanner.jsx real del SI-PIC: cuenta
// pendientes de todas las entidades registradas (ver
// registrarProveedoresSync.js), sincroniza automático al reconectar,
// evita taparle los botones a un modal abierto, y deja un botón manual
// "Sincronizar ahora".

import { useEffect, useState } from "react";
import { useOnlineStatus } from "../hooks/useSync";
import { contarTodosPendientes, sincronizarTodosPendientes } from "../../inicio/offline/syncRegistry";

function mostrarToast(texto, { exito = true } = {}) {
  const toast = document.createElement("div");
  toast.textContent = texto;
  Object.assign(toast.style, {
    position: "fixed", top: "76px", right: "12px", left: "auto", zIndex: 99999,
    background: exito ? "#198754" : "#A32D2D", color: "#fff", padding: "10px 20px",
    borderRadius: "10px", fontWeight: "600", fontSize: "0.85rem",
    boxShadow: "0 4px 12px rgba(0,0,0,0.2)", transition: "opacity 0.5s",
    maxWidth: "min(380px, calc(100vw - 24px))",
  });
  document.body.appendChild(toast);
  setTimeout(() => {
    toast.style.opacity = "0";
    setTimeout(() => toast.remove(), 500);
  }, exito ? 4000 : 6000);
}

export default function OfflineBanner() {
  const online = useOnlineStatus();
  const [pendientes,    setPendientes]    = useState(0);
  const [desglose,      setDesglose]      = useState({});
  const [sincronizando, setSincronizando] = useState(false);
  const [modalAbierto,  setModalAbierto]  = useState(false);

  // ── Detectar modal abierto (Bootstrap) para no taparle los botones ──────────
  useEffect(() => {
    const detectarModal = () => {
      const claseBody    = document.body.classList.contains("modal-open");
      const modalVisible = !!document.querySelector(".modal.show, [role='dialog']");
      setModalAbierto(claseBody || modalVisible);
    };

    detectarModal();

    const observer = new MutationObserver(detectarModal);
    observer.observe(document.body, {
      attributes: true, attributeFilter: ["class"],
      childList: true, subtree: true,
    });
    return () => observer.disconnect();
  }, []);

  // ── Contar pendientes (todas las entidades registradas) ─────────────────────
  const contar = async () => {
    try {
      const res = await contarTodosPendientes();
      setPendientes(res.total);
      setDesglose(res.desglose);
    } catch (e) {
      console.error("Error contando pendientes:", e);
    }
  };

  useEffect(() => { contar(); }, []);

  useEffect(() => {
    contar();
    if (online && pendientes > 0) handleSincronizar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [online]);

  // ── Sincronizar ───────────────────────────────────────────────────────────
  const handleSincronizar = async () => {
    if (sincronizando) return;
    setSincronizando(true);
    try {
      const { total, desglose: resultado, huboError } = await sincronizarTodosPendientes();

      if (total > 0) {
        const detalles = Object.values(resultado)
          .filter(r => r.cantidad > 0)
          .map(r => `${r.cantidad} ${r.etiqueta}`)
          .join(" · ");
        mostrarToast(`✅ ${total} registro(s) sincronizados — ${detalles}`);
      }

      if (huboError) {
        mostrarToast("⚠️ Algunos registros no se pudieron sincronizar. Revisa tu conexión e inténtalo de nuevo.", { exito: false });
      }
    } catch (e) {
      console.error("Error al sincronizar:", e);
      mostrarToast("⚠️ Hubo un problema al sincronizar. Se reintentará más tarde.", { exito: false });
    } finally {
      // Siempre recontar, haya salido bien o mal — así el banner nunca se
      // queda mostrando un número desactualizado.
      await contar();
      setSincronizando(false);
    }
  };

  if ((online && pendientes === 0) || modalAbierto) return null;

  const partes = Object.values(desglose)
    .filter(d => d.cantidad > 0)
    .map(d => `${d.cantidad} ${d.etiqueta}`)
    .join(" · ");

  return (
    <div style={{
      position: "fixed", bottom: 16, left: "50%", transform: "translateX(-50%)",
      background: online ? "#198754" : "#dc3545", color: "#fff",
      padding: "10px 18px", borderRadius: 24, fontSize: "0.85rem", fontWeight: 600,
      boxShadow: "0 4px 12px rgba(0,0,0,.25)", zIndex: 1050,
      display: "flex", alignItems: "center", gap: 12, maxWidth: "92vw",
    }}>
      <span style={{ fontSize: "1.1rem" }}>{online ? "🟢" : "📴"}</span>

      <div>
        <div>{online ? "Conexión restaurada" : "Sin conexión a internet"}</div>
        {partes && (
          <div style={{ fontSize: "0.72rem", opacity: 0.9 }}>
            {online
              ? `${pendientes} pendiente(s) de sincronizar — ${partes}`
              : `${pendientes} guardado(s) localmente — ${partes}`}
          </div>
        )}
      </div>

      {online && pendientes > 0 && (
        <button
          onClick={handleSincronizar}
          disabled={sincronizando}
          style={{
            background: "rgba(255,255,255,.2)", border: "1px solid rgba(255,255,255,.5)",
            color: "#fff", borderRadius: 16, padding: "5px 12px", fontSize: "0.78rem",
            fontWeight: 700, cursor: sincronizando ? "default" : "pointer", whiteSpace: "nowrap",
          }}
        >
          {sincronizando ? "⏳ Sincronizando..." : "🔄 Sincronizar ahora"}
        </button>
      )}
    </div>
  );
}
